// Import React Libraries
import React, { useEffect } from "react";
// import { onePageCheckOut } from '../../../providers/Redux/Reducers/reducers2';
import { useHistory } from "react-router";

// Import Styles
import styles from "./Login.module.scss";
import classNames from "classnames/bind";
import commonFormStyles from "../../../components/UI/CommonFormElements/CommonFormElements.module.scss";

// Import Ionic
import { IonButton, IonLoading, IonInput } from "@ionic/react";

import { PasswordInput } from "../../../components/PasswordInput/PasswordInput";
import MaxWidthContainer from "../../../components/UI/MaxWidthContainer/MaxWidthContainer";
import CommonFormElement from "../../../components/UI/CommonFormElements/CommonFormElement";
import { InputChangeEventDetail } from "@ionic/core/components";
import { Controller, FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { normalizeLogin } from "./utils";
import { useLoginMutation } from "../../../providers/Auth";
import { useAuth } from "../../../contexts/auth-context";
import { useConfig } from "../../../providers/config";
import { AppMetricaProvider } from "../../../providers/AppMetrica/AppMetrica";
import Page from "../../page-layout";
import { useTranslation } from "react-i18next";

const cx = classNames.bind(styles);
const commonForm = classNames.bind(commonFormStyles);

interface ISignInCredentials {
  username: string;
  password: string;
}

const CREDENTIALS_ERRORS = ["invalid_username", "invalid_email", "incorrect_password"];

const Login: React.FC = (props: any) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { login } = useAuth();

  const { mutate, isLoading } = useLoginMutation();
  const config = useConfig();

  const {
    handleSubmit,
    control,
    reset,
    setError,
    formState: { errors },
  } = useForm<FieldValues>({
    mode: "onSubmit",
  });

  const handleLogin: SubmitHandler<FieldValues> = (data) => {
    const { password, username } = data as ISignInCredentials;

    const normalizedLogin = normalizeLogin(username);

    mutate(
      { password, username: normalizedLogin },
      {
        onError: (error) => {
          if (CREDENTIALS_ERRORS.includes(error.response.data.code) || error.response.status === 401) {
            setError("password", { message: t("Invalid username or password.") });
          } else {
            setError("password", { message: t(error.response.data.message) });
          }
        },
        onSuccess: (data) => {
          AppMetricaProvider.reportEvent("login", { ...data, token: undefined });

          login(data);
          history.goBack();
        },
      },
    );
  };

  useEffect(() => {
    reset();
  }, [props.match.params.name]);

  const openSignUpPage = () => {
    history.push("/sign-up");
  };

  const openForgotPage = () => {
    history.push("/forgot-password");
  };

  return (
    <Page>
      <Page.HeaderMobile showBackButton />

      <Page.Content>
        <Page.HeaderDesktop />
        <IonLoading isOpen={isLoading} message={t("Please wait...")} />
        <div className={`${cx("about-us", "loginContent")} global-content`}>
          <MaxWidthContainer maxWidthBreakpoint={"Sm"} classList={cx("loginForm")}>
            <form onSubmit={handleSubmit(handleLogin)}>
              <Controller
                name="username"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CommonFormElement
                    label={
                      config?.loginPage.phoneOrEmailFieldEnabled
                        ? t("Username, phone number or email")
                        : t("Phone number")
                    }
                  >
                    <IonInput
                      required
                      type="text"
                      value={value}
                      className={commonForm("commonElement")}
                      placeholder={
                        config?.loginPage.phoneOrEmailFieldEnabled
                          ? t("Username, phone number or email")
                          : t("Phone number")
                      }
                      onIonChange={(e: CustomEvent<InputChangeEventDetail>) => onChange(e.detail.value!)}
                    />
                  </CommonFormElement>
                )}
              />

              <Controller
                name="password"
                control={control}
                rules={{
                  required: t("Required field"),
                }}
                render={({ field: { onChange, value } }) => (
                  <CommonFormElement
                    label={t("Password")}
                    errorMessage={errors.password?.message}
                    isValid={!errors.password?.message && value}
                  >
                    <PasswordInput
                      value={value}
                      placeholder={t("Enter password")}
                      className={commonForm("commonElement")}
                      onIonChange={(e: any) => onChange(e.detail.value)}
                    />
                  </CommonFormElement>
                )}
              />
              <input type="submit" hidden />
              <IonButton
                color="secondary"
                className="ion-text-capitalize ion-margin-start ion-margin-end"
                expand="block"
                type="submit"
              >
                {t("Login button")}
              </IonButton>
            </form>

            <IonButton expand="full" fill="clear" color="dark" onClick={() => openForgotPage()}>
              {t("Forgotten password?")}
            </IonButton>
            <IonButton
              onClick={() => openSignUpPage()}
              className="ion-margin-start ion-margin-end ion-margin-top"
              style={{ color: "black" }}
              expand="block"
              fill="outline"
            >
              {t("Register")}
            </IonButton>
          </MaxWidthContainer>
        </div>
        <Page.ContentFooter />
      </Page.Content>
    </Page>
  );
};

export default Login;
